import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "@/auth/auth-provider";
import { GridCsvColumnGuide } from "@/components/grid-csv-import-controls";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { api, type CsvImportResult, type GridImportKind, type GridImportReview } from "@/lib/api";

const headers: Record<GridImportKind, readonly string[]> = {
  feeders: [
    "external_key", "feeder_name", "distribution_company_external_key", "substation_external_key",
    "display_name", "voltage_kv", "upstream_feeder_name", "upstream_voltage_kv",
    "source_voltage_kv", "feeder_location", "major_streets", "served_states", "regulators",
    "nerc_service_classes", "metered_customers", "unmetered_customers", "iot_vendor",
    "iot_phase", "iot_installed", "source_metadata", "feeder_type", "feeder_type_detail",
    "latitude", "longitude", "power_transformer_id", "power_transformer_capacity_mva",
    "feeder_band", "feeder_voltage", "ct_vt_condition", "ct_primary", "ct_secondary",
    "vt_primary", "vt_secondary", "ct_verification_state", "ct_verification_source",
    "vt_verification_state", "vt_verification_source", "station_dc_nominal_v",
    "station_dc_min_v", "station_dc_max_v", "preferred_network",
    "installation_type_available", "remarks",
  ],
  substations: [
    "distribution_company_external_key", "external_key", "name", "base_name", "station_type",
    "voltage_kv", "address_line", "city", "state", "served_states", "tcn_region",
    "tcn_subregion", "transmission_station", "injection_substation", "latitude", "longitude",
  ],
};

export function GridImportPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const kind: GridImportKind = params.get("type") === "substations" ? "substations" : "feeders";
  const { me } = useAuth();
  const client = useQueryClient();
  const [content, setContent] = useState<string | null>(null);
  const [filename, setFilename] = useState("");
  const [review, setReview] = useState<GridImportReview | null>(null);
  const [result, setResult] = useState<CsvImportResult | null>(null);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const busy = useRef(false);
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
    setContent(null);
    setFilename("");
    setReview(null);
    setResult(null);
    setError("");
  }, [kind, me?.tenant_id]);

  async function chooseFile(file: File | undefined): Promise<void> {
    const selected = ++generation.current;
    setContent(null);
    setFilename(file?.name ?? "");
    setReview(null);
    setResult(null);
    setError("");
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setError("CSV exceeds the 2 MiB limit");
      return;
    }
    try {
      const text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            if (!(reader.result instanceof ArrayBuffer)) throw new Error("Could not read CSV file");
            resolve(new TextDecoder("utf-8", { fatal: true }).decode(reader.result));
          } catch {
            reject(new Error("CSV must use UTF-8 encoding"));
          }
        };
        reader.onerror = () => reject(new Error("Could not read CSV file"));
        reader.readAsArrayBuffer(file);
      });
      if (generation.current === selected) setContent(text);
    } catch (failure: unknown) {
      if (generation.current === selected) setError(failure instanceof Error ? failure.message : "Could not read file");
    }
  }

  async function run(action: "preview" | "commit"): Promise<void> {
    if (busy.current || content === null || (action === "commit" && !review?.fingerprint)) return;
    busy.current = true;
    setPending(true);
    setError("");
    const selected = generation.current;
    try {
      if (action === "preview") {
        const value = await api.previewGridImport(kind, content);
        if (generation.current === selected) setReview(value);
      } else if (review?.fingerprint) {
        const value = await api.commitGridImport(kind, content, review.fingerprint);
        if (generation.current === selected) {
          setResult(value);
          setReview(null);
          void client.invalidateQueries({ queryKey: ["feeders"] });
          void client.invalidateQueries({ queryKey: ["substations"] });
        }
      }
    } catch (failure: unknown) {
      if (generation.current === selected) {
        setError(failure instanceof Error ? failure.message : "Import failed");
        if (action === "commit") setReview(null);
      }
    } finally {
      busy.current = false;
      setPending(false);
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Import catalog data" description="Select a type, validate a CSV file, and review changes before import." />
      <label className="block">Data type{" "}
        <select value={kind} disabled={pending} onChange={(event) => setParams({ type: event.target.value })}>
          <option value="feeders">Feeders</option><option value="substations">Substations</option>
        </select>
      </label>
      <Button asChild variant="outline">
        <a download={`${kind}-template.csv`} href={`data:text/csv;charset=utf-8,${encodeURIComponent(`${headers[kind].join(",")}\n`)}`}>Download CSV template</a>
      </Button>
      <GridCsvColumnGuide entityLabel={kind === "feeders" ? "Feeder" : "Substation"} />
      <label className="block">CSV file{" "}
        <input type="file" accept=".csv,text/csv" disabled={pending} onChange={(event) => void chooseFile(event.currentTarget.files?.[0])} />
      </label>
      {filename ? <p>Selected: {filename}</p> : null}
      <Button disabled={pending || content === null} onClick={() => void run("preview")}>Validate file</Button>
      {error ? <p role="alert">{error}</p> : null}
      {review ? <section aria-label="Import review" className="space-y-3">
        <h2 className="font-semibold">Review</h2>
        <p>
          {review.rows.filter((row) => row.action === "create").length} to create,{" "}
          {review.rows.filter((row) => row.action === "update").length} to update.
        </p>
        {review.errors.map((item, index) => <p role="alert" key={`${item.row}-${index}`}>Row {item.row}: {item.message}</p>)}
        {review.errors_truncated ? <p>Additional errors were not displayed.</p> : null}
        {review.rows.map((row) => <div key={row.row} className="rounded border p-3">
          <p>Row {row.row}: {row.action} {row.external_key} ({row.company_key}{row.substation_key ? ` / ${row.substation_key}` : ""})</p>
          <ul>{Object.entries(row.changes).map(([field, change]) => <li key={field}>{field}: {JSON.stringify(change.before)} → {JSON.stringify(change.after)}</li>)}</ul>
        </div>)}
        <Button disabled={pending || !review.fingerprint} onClick={() => void run("commit")}>Confirm import</Button>
      </section> : null}
      {result ? <p role="status">Import complete: {result.created} created, {result.updated} updated.</p> : null}
    </div>
  );
}
